import { appUtils } from 'lib/AppUtils'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import validator from 'validator'

import { ButtonWithLoading } from '@/components/buttons/ButtonWithLoading'
import { TextInput } from '@/components/FormElement'
import { useLanguage } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'

import { ModalContainer } from './Modal'

type MergeStep = 'choose' | 'email' | 'sent'

interface MergeAccountModalProps {
  onClose: () => void
  /** 预填邮箱：用户在账户详情页补全邮箱时撞到已有账号，直接进入邮箱确认步骤 */
  presetEmail?: string
}

const MergeAccountModal: FC<MergeAccountModalProps> = ({ onClose, presetEmail }) => {
  const { merge_account: mergeStr, errors: errStr, auth: authStr, actions: actStr } = useLanguage()
  const [step, setStep] = useState<MergeStep>(presetEmail ? 'email' : 'choose')
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm({
    mode: 'onBlur',
    defaultValues: { email: presetEmail ?? '' },
  })

  /** User has old account — POST email to merge-account/request */
  const handleSendVerification = async (data: { email: string }) => {
    const res = await appUtils.postData(API_URL.mergeAccountRequest, { email: data.email }, errStr, true, setIsLoading)
    if (res !== null) {
      setStep('sent')
    }
  }

  return (
    <ModalContainer show={true} onClose={onClose} innerProps={{ className: 'md:w-1/2 lg:w-96' }}>
      <div className='flex flex-col items-center'>
        {/* Step 1: Choose */}
        {step === 'choose' && (
          <>
            <p className='mb-2 text-center text-lg font-semibold'>{mergeStr.title}</p>
            <p className='mb-6 text-center text-sm text-gray-600'>{mergeStr.description}</p>
            <div className='flex w-full flex-col gap-3'>
              <ButtonWithLoading
                title={mergeStr.has_account}
                isLoading={false}
                className='btn-rd-amber btn-hover-amber h-10 w-full'
                onClick={() => setStep('email')}
                data-testid='btn-merge-has-account'
              />
              <ButtonWithLoading
                title={mergeStr.no_account}
                isLoading={false}
                className='btn-rd-outline btn-hover-amber h-10 w-full'
                onClick={onClose}
                data-testid='btn-merge-no-account'
              />
            </div>
          </>
        )}

        {/* Step 2: Email input */}
        {step === 'email' && (
          <form onSubmit={handleSubmit(handleSendVerification)} className='w-full'>
            <p className='mb-2 text-center text-lg font-semibold'>{mergeStr.title}</p>
            <p className='mb-4 text-center text-sm text-gray-600'>{presetEmail ? mergeStr.email_taken_prompt : mergeStr.enter_email}</p>
            <TextInput
              outerCls='mb-4'
              label={authStr.email}
              required
              input={{
                'type': 'email',
                'placeholder': mergeStr.email_placeholder,
                ...register('email', {
                  // Trim value read from the input: iOS keyboards / Facebook in-app browser autofill
                  // often append whitespace, which makes validator.isEmail() reject a valid address.
                  setValueAs: value => (typeof value === 'string' ? value.trim() : value),
                  required: authStr.email + errStr.required,
                  validate: value => validator.isEmail(value) || errStr.email_invalid,
                }),
                // @ts-ignore
                'data-testid': 'inp-merge-email',
              }}
              showValidated={!!errors?.email}
              valid={!errors?.email}
              tipMessage={errors?.email?.message?.toString() ?? undefined}
            />
            <div className='flex w-full flex-col gap-3'>
              <ButtonWithLoading
                type='submit'
                title={mergeStr.send_verification}
                isLoading={isLoading}
                disabled={!isValid}
                className='btn-rd-amber btn-hover-amber btn-disabled-grey h-10 w-full'
                data-testid='btn-merge-send'
              />
              <button
                type='button'
                className='btn-rd-outline btn-hover-amber h-10 w-full'
                onClick={() => (presetEmail ? onClose() : setStep('choose'))}
                data-testid='btn-merge-back'
              >
                {presetEmail ? actStr.cancel : mergeStr.back}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Email sent confirmation */}
        {step === 'sent' && (
          <>
            <div className='mb-4 flex justify-center'>
              <svg className='h-16 w-16 text-green-500' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} aria-hidden='true'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
            </div>
            <p className='mb-2 text-center text-lg font-semibold'>{authStr.email_verification}</p>
            <p className='mb-6 text-center text-sm text-gray-600'>{mergeStr.email_sent}</p>
            <button className='btn-rd-amber btn-hover-amber h-10 w-full' onClick={onClose} data-testid='btn-merge-done'>
              {actStr.close}
            </button>
          </>
        )}
      </div>
    </ModalContainer>
  )
}

export const MergeCompleteModal: FC<MergeAccountModalProps> = ({ onClose }) => {
  const { merge_account: mergeStr, actions: actStr } = useLanguage()

  return (
    <ModalContainer show={true} onClose={onClose} innerProps={{ className: 'md:w-1/2 lg:w-96' }}>
      <div className='flex flex-col items-center'>
        <div className='mb-4 flex justify-center'>
          <svg className='h-16 w-16 text-green-500' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} aria-hidden='true'>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        </div>
        <p className='mb-2 text-center text-lg font-semibold'>{mergeStr.merge_complete}</p>
        <p className='mb-6 text-center text-sm text-gray-600'>{mergeStr.merge_complete_description}</p>
        <button className='btn-rd-amber btn-hover-amber h-10 w-full' onClick={onClose} data-testid='btn-merge-complete-confirm'>
          {actStr.confirm}
        </button>
      </div>
    </ModalContainer>
  )
}

export default MergeAccountModal
